import React, { useState, useRef, useEffect } from "react";
import { MediaRecorder, register } from "extendable-media-recorder";
import { connect } from "extendable-media-recorder-wav-encoder";

interface RecordAudioProps {
  onRecordingFinished: (audioFile: File) => void;
  onRecordingStart: () => void;
}

export const RecordAudio: React.FC<RecordAudioProps> = ({
  onRecordingFinished,
  onRecordingStart,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<any | null>(null);
  const chunksRef = useRef<Blob[]>([]); // To store audio chunks
  const startTimeRef = useRef<number | null>(null); // To track the start time
  const timerRef = useRef<NodeJS.Timeout | null>(null); // To track the 30-second timer

  useEffect(() => {
    if (initialized) return;
    const initializeEncoder = async () => {
      await register(await connect());
    };
    initializeEncoder()
      .then((r) => null)
      .catch((e) => console.error(e));
    setInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRecording = async () => {
    try {
      // Clear previous errors
      setError(null);

      // Request audio stream from the device
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: false,
          echoCancellation: false,
          autoGainControl: false,
          channelCount: 1
        }
      });
      

      const audioContext = new AudioContext({ sampleRate: 44100 });
      const mediaStreamAudioSourceNode = new MediaStreamAudioSourceNode(
        audioContext,
        { mediaStream: stream },
      );
      const mediaStreamAudioDestinationNode =
        new MediaStreamAudioDestinationNode(audioContext);
      mediaStreamAudioSourceNode.connect(mediaStreamAudioDestinationNode);

      const mediaRecorder = new MediaRecorder(
        mediaStreamAudioDestinationNode.stream,
        {
          audioBitsPerSecond: 256000,
          mimeType: "audio/wav",
        },
      );
      mediaRecorderRef.current = mediaRecorder;

      onRecordingStart();

      const { sampleRate } = stream.getAudioTracks()[0].getSettings();
      console.log("Sample rate:", sampleRate);

      // Listen for audio data availability
      mediaRecorder.ondataavailable = (event) => {
        chunksRef.current.push(event.data);
      };

      // Handle the stop event (called manually or by the 30-second limit)
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/wav" });
        const audioFile = new File([blob], `recording.wav`, {
          type: "audio/wav",
        });
        chunksRef.current = [];
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }
        onRecordingFinished(audioFile);
      };

      // Start recording
      mediaRecorder.start();
      startTimeRef.current = Date.now();
      setIsRecording(true);

      console.log("Recording started...");

      // Automatically stop the recording after 30 seconds
      timerRef.current = setTimeout(() => {
        console.log("Recording timed out.");
        stopRecording();
      }, 30000);
    } catch (error) {
      setError("Unable to access microphone. Please grant permissions.");
      console.error(error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      const elapsedTime = Date.now() - (startTimeRef.current || 0);

      if (elapsedTime < 3000) {
        setError("Recording must be at least 3 seconds long.");
        return;
      }

      // Stop the MediaRecorder
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
      setIsRecording(false);

      // Clear the 30-second timer if it's still active
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  // Clean up if the component unmounts while recording
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
      if (timerRef.current && isRecording) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isRecording]);

  return (
    <div className="record-audio bg-support-gray flex flex-col p-6 items-center rounded-xl">
      <button
        className={`custom-button ${isRecording ? "recording" : ""}`}
        onClick={isRecording ? stopRecording : startRecording}
      >
        <span className="inner-circle"></span>
      </button>

      {/* Error feedback */}
      {error && <p className="text-red-500 mt-2">{error}</p>}

      {/* Recording visual feedback */}
      {!isRecording && (
        <p className="font-bold mt-2 text-sm">Tap to Start Recoding</p>
      )}
      {isRecording && (
        <p className="font-bold text-progress-ok text-sm mt-2">
          Recording in Progress, Tap to Stop
        </p>
      )}
    </div>
  );
};
